import { ContentObject } from '@varos/research-sdk-api';
import { SidebarTreeNode } from './reportingSidebarModel';
import { HiOutlineDocumentText } from 'react-icons/hi';
// Recursive helper to build children (returns SidebarTreeNode[])
function buildContentChildrenRecursive(
    parentId: string,
    parentMap: Record<string, ContentObject[]>
): SidebarTreeNode[] | null {
    const children = parentMap[parentId];
    if (!children || children.length === 0) {
        return null;
    }
    return children.map(
        (item): SidebarTreeNode => ({
            id: item.id,
            title: item.title ?? 'Untitled',
            icon: item.icon
                ? {
                      kind: 'emoji',
                      emoji: item.icon,
                  }
                : {
                      kind: 'icon',
                      Component: HiOutlineDocumentText,
                  },
            children: buildContentChildrenRecursive(item.id, parentMap),
            content: item,
        })
    );
}

/**
 * Builds a simple hierarchical tree from a flat list of ContentObjects
 * using the parent property.
 *
 * @param contentList - The flat list of ContentObjects.
 * @returns An array of SidebarTreeNode representing the root content nodes.
 */
export function buildSidebarTree(contentList: ContentObject[]): SidebarTreeNode[] {
    // 1. Build parentMap for efficient child lookup (content parent -> content children)
    const parentMap: Record<string, ContentObject[]> = {};
    contentList.forEach((item) => {
        // Only map items that are children of other content items
        if (item.parent && item.parent !== null) {
            const parentId = String(item.parent);
            if (!parentMap[parentId]) {
                parentMap[parentId] = [];
            }
            parentMap[parentId].push(item);
            parentMap[parentId].sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
        }
    });

    // 2. Find root items (parent === null) and build the tree
    const finalTree = contentList
        .filter((item) => item.parent === null) // Filter for root items
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)) // Sort root items
        .map(
            (rootItem): SidebarTreeNode => ({
                id: rootItem.id,
                title: rootItem.title ?? 'Untitled',
                icon: rootItem.icon
                    ? {
                          kind: 'emoji',
                          emoji: rootItem.icon,
                      }
                    : null,
                children: buildContentChildrenRecursive(rootItem.id, parentMap),
                content: rootItem,
            })
        );

    return finalTree;
}

// Helper to calculate pixel value from CSS string (handles px, rem, and viewport units)
export function calculatePixelValue(
    value: string | number | undefined,
    defaultValue: number
): number {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value !== 'string') {
        return defaultValue;
    }

    const trimmedValue = value.trim();

    // Handle px
    if (trimmedValue.endsWith('px')) {
        return parseInt(trimmedValue, 10) || defaultValue;
    }

    // Handle rem (assuming 1rem = 16px)
    if (trimmedValue.endsWith('rem')) {
        const num = parseFloat(trimmedValue);
        return !isNaN(num) ? num * 16 : defaultValue;
    }

    // Handle viewport units
    const match = trimmedValue.match(/^([+-]?\d*\.?\d+)(vw|vh|dvw|dvh|vmin|vmax)$/);
    if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2];

        // Need access to window, ensure this helper is only used client-side
        if (typeof window === 'undefined') {
            return defaultValue;
        }
        const { innerWidth, innerHeight } = window;

        switch (unit) {
            case 'vw':
            case 'dvw':
                return (num / 100) * innerWidth;
            case 'vh':
            case 'dvh':
                return (num / 100) * innerHeight;
            case 'vmin':
                return (num / 100) * Math.min(innerWidth, innerHeight);
            case 'vmax':
                return (num / 100) * Math.max(innerWidth, innerHeight);
            default:
                return defaultValue;
        }
    }

    // Handle unitless number string (assume px)
    const num = parseInt(trimmedValue, 10);
    if (!isNaN(num) && num.toString() === trimmedValue) {
        return num;
    }

    // Return default for unhandled units (rem, em, percentages, etc.) or invalid strings
    console.warn(
        `calculatePixelValue could not parse: "${value}". Using default: ${defaultValue}`
    );
    return defaultValue;
}

// Check if a css value string uses viewport units (or might be a responsive object)
export function checkNeedsResizeListener(value: any): boolean {
    if (typeof value === 'string') {
        return /vw|vh|dvw|dvh|vmin|vmax/.test(value.trim());
    }
    // Assume responsive objects might contain viewport units
    if (typeof value === 'object' && value !== null) {
        return true;
    }
    return false;
}
